/**
 * Typed Workspace API client: the granular REST routes "Open in Nanome" needs, as open-in-nanome-2 proved them
 * (vendor-ref/open-in-nanome-2/pipeline.js:1022-1053 client, :1108-1321 call order) against
 * WorkspaceAPI/src/Infrastructure/API/REST/RestApi.cs (routes) and RestApi.Delegates.cs (handlers).
 *
 * Rules (research sections open-in-nanome-bridge__03.md, __05.md, __07.md):
 *  - every request sends `Authorization: Bearer <token>`; JSON bodies add Content-Type: application/json; multipart
 *    bodies are a FormData and fetch writes the boundary itself (pipeline.js:1030-1032)
 *  - REST errors are {message, code?}: 403 PermissionException, 404 KeyNotFound, else 400, with code 'LICENSE_ERROR'
 *    for license limits (RestApi.cs:195-228); 401 means the token is gone (AuthExpired)
 *  - the global concurrency limiter answers 503 'Server too busy. Please retry.' (DependencyInjectionContainer.cs:113-140):
 *    retried 3 times with backoff
 *  - 60 s per request by default (GraphQL ExecutionTimeout is 60 s, GraphQlServer.cs:74-77; the gateway cuts a
 *    single request at ~5 min, OIN/README.md:146-150), uploads get a longer budget
 */
import type { ComponentInput, FilterModel, VisualizationModel } from '@nanome-lite/state';
import { ApiError, AuthExpired, isAbortError } from './errors';
import { trimSlash, type FetchLike } from './discovery';

// --- server models (WorkspaceAPI/src/Adapter/API/**) ------------------------------------------------------------------

/** WorkspaceAPI/src/Adapter/API/Workspaces/WorkspaceModel.cs:5-19 (+ BaseModelSoftDelete: id, createdAt, updatedAt, deletedAt). */
export interface WorkspaceModel {
  id: string;
  projectId: string;
  name: string;
  description: string;
  entries?: EntryModel[];
  nextEntrySerial: number;
  nextSceneSerial: number;
  permissions?: PermissionModel[];
  nextTagKey?: number;
  tagDefinitions?: Record<string, string>;
  /** Share code (used by POST /workspaces/lookup and {mara}/join/{code}). */
  code: string;
  sharingLevel?: number;
  isPublic?: boolean;
  createdAt?: string;
  updatedAt?: string;
  ownerName?: string;
  lastAccessedAt?: string | null;
}

/** GET /workspaces returns WorkspaceSummaryModel[] (RestApi.Delegates.cs:72-86). */
export interface WorkspaceSummary {
  id: string;
  projectId: string;
  name: string;
  description: string;
  ownerName: string;
  createdAt?: string;
  updatedAt?: string;
  lastAccessedAt?: string | null;
}

/** WorkspaceAPI/src/Adapter/API/Workspaces/EntryModel.cs. `models` are model GUIDs, one per frame/model. */
export interface EntryModel {
  serial: number;
  name: string;
  models: string[];
  createdAt?: string;
  updatedAt?: string;
}

/** WorkspaceAPI/src/Adapter/API/Scenes/SceneComponentModel.cs. */
export interface SceneComponentModel {
  serial: number;
  name: string;
  filters: FilterModel[];
  visualizations: VisualizationModel[];
  hidden: boolean;
}

/** WorkspaceAPI/src/Adapter/API/Scenes/SceneModel.cs:5-18. */
export interface SceneModel {
  id: string;
  serial: number;
  orderSerial: number;
  workspaceId: string;
  name: string;
  transform?: unknown;
  components?: SceneComponentModel[];
  annotations?: unknown[];
  entryTransforms?: unknown[];
  entryModelSerial?: { entrySerial: number; modelSerial: number }[];
}

/** WorkspaceAPI/src/Adapter/API/Permissions/PermissionModel.cs. Levels: None=0, Viewer=1, Editor=2, Owner=3 (Nanome.Domain Permission.cs:8-14). */
export interface PermissionModel {
  workspaceId?: string | null;
  projectId?: string | null;
  permissionLevel: number;
  user?: string | null;
  email?: string | null;
}

export const PERMISSION_OWNER = 3;

/**
 * GET {accounts}/user/session?slim=true, as the Workspace API itself checks tokens
 * (WorkspaceAPI/src/Infrastructure/Accounts/AccountsApi.cs:34-80, 168-171). The accounts API wraps answers as
 * {success, error?, results}; `results` holds {user:{unique,name,email}, token:{value,expired_time},
 * organization?:{id,name,accepted}, license?:{type,expired_time}}.
 */
export interface AccountSession {
  user: { id: string; name: string; email: string };
  token?: { value?: string; expiredAt?: string };
  organization?: { id: number; name: string; accepted?: boolean } | null;
  /** Accounts license type, lower-cased: free | trial | plus | edu | pro | enterprise; null when absent (= unrestricted, LicenseType.cs). */
  licenseType: string | null;
  licenseExpiresAt?: string | null;
  raw: unknown;
}

export interface ComponentSchemas {
  $schema?: string;
  title?: string;
  description?: string;
  $defs: Record<string, unknown>;
  presets?: unknown;
  [key: string]: unknown;
}

// --- client -------------------------------------------------------------------------------------------------------

export interface WorkspaceApiOptions {
  /** workspace_api_url from GET {mara}/api/info. */
  baseUrl: string;
  /** OAuth access token (an accounts API key) or a pasted API key. */
  token: string;
  fetch?: FetchLike;
  /** Per-request timeout; default 60 s. */
  timeoutMs?: number;
  /** Timeout for file uploads; default 300 s (MARA's own httpx timeout, mara/nanome/tools.py:14). */
  uploadTimeoutMs?: number;
  /** Backoff before each 503 retry; default [500, 1500, 4000] ms, so 3 retries. */
  retryDelaysMs?: number[];
  /** Aborts every request made through this client. Per-call signals add to it. */
  signal?: AbortSignal;
  /** Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
}

export interface CallOptions { signal?: AbortSignal; step?: string }

export interface UploadFile { name: string; bytes: Uint8Array }

export const DEFAULT_TIMEOUT_MS = 60_000;
export const DEFAULT_UPLOAD_TIMEOUT_MS = 300_000;
export const DEFAULT_RETRY_DELAYS_MS = [500, 1500, 4000];

export interface WorkspaceApi {
  readonly baseUrl: string;
  /** GET /workspaces (RestApi.cs:321). */
  listWorkspaces(opts?: CallOptions): Promise<WorkspaceSummary[]>;
  /** GET /workspaces/own-permissions (RestApi.cs:327): one row per workspace/project the user has a permission on; owned = level 3. */
  getOwnPermissions(opts?: CallOptions): Promise<PermissionModel[]>;
  /** POST /workspaces {name, description, projectId?} (RestApi.cs:322; CreateWorkspace.cs:32-103). One scene is created with it. */
  createWorkspace(input: { name: string; description?: string; projectId?: string }, opts?: CallOptions): Promise<WorkspaceModel>;
  /** GET /workspaces/{id} (RestApi.cs:333). */
  getWorkspace(id: string, opts?: CallOptions): Promise<WorkspaceModel>;
  /** DELETE /workspaces/{id} (RestApi.cs:335) -> the deleted id. */
  deleteWorkspace(id: string, opts?: CallOptions): Promise<string>;
  /**
   * POST /workspaces/{id}/entries/load-multiple (RestApi.cs:353; RestApi.Delegates.cs:163-175): multipart parts named
   * `files`. ONE file per request is the proven practice (OIN/README.md:146-150); pass several only for tiny files.
   * Returns the EntryModel[] the server created; take serials from it, never assume 1..N.
   */
  loadEntries(id: string, files: UploadFile[], opts?: CallOptions): Promise<EntryModel[]>;
  /** GET /workspaces/{id}/scenes (RestApi.Delegates.cs:325-326). Pick the lowest serial (pipeline.js:1313-1315). */
  listScenes(id: string, opts?: CallOptions): Promise<SceneModel[]>;
  /** POST .../scenes/{s}/entries/{e}/components/add-default, or scene-wide .../scenes/{s}/components/add-default when entrySerial is omitted (RestApi.cs:419, 429). */
  addDefaultComponents(id: string, sceneSerial: number, entrySerial?: number, opts?: CallOptions): Promise<SceneModel>;
  /** POST .../scenes/{s}/components/add with ONE ComponentInput (RestApi.Delegates.cs:411-419). Returns the full SceneModel. */
  addComponent(id: string, sceneSerial: number, component: ComponentInput, opts?: CallOptions): Promise<SceneModel>;
  /** GET .../scenes/{s}/components. */
  getComponents(id: string, sceneSerial: number, opts?: CallOptions): Promise<SceneComponentModel[]>;
  /** POST .../scenes/{s}/components/toggle-hidden {componentSerials, hidden} (RestApi.cs:135-139). */
  toggleHidden(id: string, sceneSerial: number, componentSerials: number[], hidden: boolean, opts?: CallOptions): Promise<unknown>;
  /** POST .../scenes/{s}/components/{c}/update-visualization with a VisualizationModel body (RestApi.Delegates.cs:654-674). */
  updateVisualization(id: string, sceneSerial: number, componentSerial: number, visualization: VisualizationModel, opts?: CallOptions): Promise<unknown>;
  /** POST .../scenes/{s}/entries/{e}/model-serial {modelSerial} (RestApi.cs:120-123). Entries open on model 1 by default (LoadMolecules.cs:175-180). */
  setModelSerial(id: string, sceneSerial: number, entrySerial: number, modelSerial: number, opts?: CallOptions): Promise<unknown>;
  /** POST /workspaces/{id}/set-public {isPublic} -> bool (RestApi.cs:130-133). Owner only. */
  setPublic(id: string, isPublic: boolean, opts?: CallOptions): Promise<boolean>;
  /** POST /workspaces/{id}/queue -> Guid; the XR app opens it on next launch (RestApi.cs:340). */
  queue(id: string, opts?: CallOptions): Promise<string>;
  /** POST /workspaces/lookup {code} -> workspace GUID as a bare JSON string (RestApi.cs:325, AllowAnonymous). */
  lookup(code: string, opts?: CallOptions): Promise<string>;
  /** GET /schemas (RestApi.cs:268, AllowAnonymous): JSON Schema 2020-12 with $defs component/filter/visualization/coloring and presets. */
  getSchemas(opts?: CallOptions): Promise<ComponentSchemas>;
  /** GET {accounts}/user/session?slim=true with the same Bearer token. 401 -> AuthExpired. */
  getSession(accountsUrl: string, opts?: CallOptions): Promise<AccountSession>;
  /** Low-level: a JSON request against {baseUrl}{path}. */
  request<T>(step: string, method: string, url: string, options?: RequestOptions): Promise<T>;
}

export interface RequestOptions {
  json?: unknown;
  form?: FormData;
  auth?: boolean;
  headers?: Record<string, string>;
  signal?: AbortSignal | undefined;
  timeoutMs?: number;
  /** Parse the body as JSON (default) or return the raw text. */
  parse?: 'json' | 'text';
}

const defaultSleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

export function createWorkspaceApi(options: WorkspaceApiOptions): WorkspaceApi {
  const baseUrl = trimSlash(options.baseUrl);
  const token = options.token;
  const fetchImpl: FetchLike = options.fetch ?? ((i, init) => globalThis.fetch(i, init));
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const uploadTimeoutMs = options.uploadTimeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS;
  const retryDelays = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  const sleep = options.sleep ?? defaultSleep;
  const enc = encodeURIComponent;

  async function send(step: string, method: string, url: string, o: RequestOptions = {}): Promise<{ res: Response; text: string }> {
    const headers: Record<string, string> = { ...(o.headers ?? {}) };
    if ((o.auth ?? true) && token) headers.Authorization = `Bearer ${token}`;
    let body: BodyInit | undefined;
    if (o.json !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(o.json);
    } else if (o.form !== undefined) {
      body = o.form; // fetch writes the multipart boundary itself (pipeline.js:1032)
    }
    const budget = o.timeoutMs ?? timeoutMs;
    for (let attempt = 0; ; attempt++) {
      const outer = combineSignals([options.signal, o.signal]);
      const timer = new AbortController();
      const timeoutId = setTimeout(() => timer.abort(), budget);
      const signal = combineSignals([outer, timer.signal]);
      let res: Response;
      try {
        const init: RequestInit = { method, headers, signal: signal ?? null };
        if (body !== undefined) init.body = body;
        res = await fetchImpl(url, init);
      } catch (e) {
        clearTimeout(timeoutId);
        if (outer?.aborted || (isAbortError(e) && !timer.signal.aborted)) {
          throw new ApiError(step, `${method} ${url} aborted`, { status: 0, url, method, code: 'ABORTED', cause: e });
        }
        if (timer.signal.aborted) {
          throw new ApiError(step, `${method} ${url} timed out after ${budget} ms`, { status: 0, url, method, code: 'TIMEOUT', cause: e });
        }
        throw new ApiError(step, `${method} ${url} failed: ${(e as Error)?.message ?? String(e)}`, { status: 0, url, method, code: 'NETWORK', cause: e });
      }
      let text: string;
      try {
        text = await res.text();
      } finally {
        clearTimeout(timeoutId);
      }
      if (res.status === 503 && attempt < retryDelays.length) {
        await sleep(retryDelays[attempt]!);
        if (outer?.aborted) throw new ApiError(step, `${method} ${url} aborted`, { status: 0, url, method, code: 'ABORTED' });
        continue;
      }
      if (res.status === 401) throw new AuthExpired(step, url);
      if (!res.ok) {
        const parsed = parseErrorBody(text);
        const detail = parsed.message ? `: ${parsed.message}` : '';
        throw new ApiError(step, `${method} ${url} returned HTTP ${res.status}${detail}`, { status: res.status, body: text, code: parsed.code, url, method, serverMessage: parsed.message });
      }
      return { res, text };
    }
  }

  async function request<T>(step: string, method: string, url: string, o: RequestOptions = {}): Promise<T> {
    const { res, text } = await send(step, method, url, o);
    if (o.parse === 'text') return text as unknown as T;
    if (!text.trim()) return undefined as unknown as T;
    try {
      return JSON.parse(text) as T;
    } catch (e) {
      throw new ApiError(step, `${method} ${url} did not return JSON`, { status: res.status, body: text, url, method, cause: e });
    }
  }

  const ws = (id: string) => `${baseUrl}/workspaces/${enc(id)}`;
  const scene = (id: string, s: number) => `${ws(id)}/scenes/${s}`;
  const call = (step: string, opts?: CallOptions) => ({ step: opts?.step ?? step, signal: opts?.signal });

  return {
    baseUrl,
    request,
    listWorkspaces(opts) {
      const c = call('workspaces', opts);
      return request<WorkspaceSummary[]>(c.step, 'GET', `${baseUrl}/workspaces`, { signal: c.signal });
    },
    getOwnPermissions(opts) {
      const c = call('permissions', opts);
      return request<PermissionModel[]>(c.step, 'GET', `${baseUrl}/workspaces/own-permissions`, { signal: c.signal });
    },
    async createWorkspace(input, opts) {
      const c = call('workspace', opts);
      const body: { name: string; description: string; projectId?: string } = { name: input.name, description: input.description ?? '' };
      if (input.projectId) body.projectId = input.projectId;
      const w = await request<WorkspaceModel>(c.step, 'POST', `${baseUrl}/workspaces`, { json: body, signal: c.signal });
      if (!w || typeof w.id !== 'string' || !w.id) {
        throw new ApiError(c.step, 'the Workspace API returned no workspace id', { status: 200, body: JSON.stringify(w), url: `${baseUrl}/workspaces`, method: 'POST' });
      }
      return w;
    },
    getWorkspace(id, opts) {
      const c = call('workspace', opts);
      return request<WorkspaceModel>(c.step, 'GET', ws(id), { signal: c.signal });
    },
    deleteWorkspace(id, opts) {
      const c = call('cleanup', opts);
      return request<string>(c.step, 'DELETE', ws(id), { signal: c.signal });
    },
    async loadEntries(id, files, opts) {
      const c = call('load', opts);
      const form = new FormData();
      for (const f of files) form.append('files', new Blob([f.bytes as BlobPart], { type: 'application/octet-stream' }), f.name);
      const url = `${ws(id)}/entries/load-multiple`;
      const entries = await request<EntryModel[]>(c.step, 'POST', url, { form, signal: c.signal, timeoutMs: uploadTimeoutMs });
      if (!Array.isArray(entries)) throw new ApiError(c.step, 'load-multiple did not return a list of entries', { status: 200, body: JSON.stringify(entries), url, method: 'POST' });
      return entries;
    },
    listScenes(id, opts) {
      const c = call('scene', opts);
      return request<SceneModel[]>(c.step, 'GET', `${ws(id)}/scenes`, { signal: c.signal });
    },
    addDefaultComponents(id, sceneSerial, entrySerial, opts) {
      const c = call('components', opts);
      const url = entrySerial === undefined
        ? `${scene(id, sceneSerial)}/components/add-default`
        : `${scene(id, sceneSerial)}/entries/${entrySerial}/components/add-default`;
      return request<SceneModel>(c.step, 'POST', url, { signal: c.signal });
    },
    addComponent(id, sceneSerial, component, opts) {
      const c = call('components', opts);
      return request<SceneModel>(c.step, 'POST', `${scene(id, sceneSerial)}/components/add`, { json: component, signal: c.signal });
    },
    getComponents(id, sceneSerial, opts) {
      const c = call('components', opts);
      return request<SceneComponentModel[]>(c.step, 'GET', `${scene(id, sceneSerial)}/components`, { signal: c.signal });
    },
    toggleHidden(id, sceneSerial, componentSerials, hidden, opts) {
      const c = call('hidden', opts);
      return request<unknown>(c.step, 'POST', `${scene(id, sceneSerial)}/components/toggle-hidden`, { json: { componentSerials, hidden }, signal: c.signal });
    },
    updateVisualization(id, sceneSerial, componentSerial, visualization, opts) {
      const c = call('components', opts);
      return request<unknown>(c.step, 'POST', `${scene(id, sceneSerial)}/components/${componentSerial}/update-visualization`, { json: visualization, signal: c.signal });
    },
    setModelSerial(id, sceneSerial, entrySerial, modelSerial, opts) {
      const c = call('frames', opts);
      return request<unknown>(c.step, 'POST', `${scene(id, sceneSerial)}/entries/${entrySerial}/model-serial`, { json: { modelSerial }, signal: c.signal });
    },
    setPublic(id, isPublic, opts) {
      const c = call('public', opts);
      return request<boolean>(c.step, 'POST', `${ws(id)}/set-public`, { json: { isPublic }, signal: c.signal });
    },
    queue(id, opts) {
      const c = call('queue', opts);
      return request<string>(c.step, 'POST', `${ws(id)}/queue`, { signal: c.signal });
    },
    lookup(code, opts) {
      const c = call('lookup', opts);
      return request<string>(c.step, 'POST', `${baseUrl}/workspaces/lookup`, { json: { code }, auth: false, signal: c.signal });
    },
    getSchemas(opts) {
      const c = call('schemas', opts);
      return request<ComponentSchemas>(c.step, 'GET', `${baseUrl}/schemas`, { auth: false, signal: c.signal });
    },
    async getSession(accountsUrl, opts) {
      const c = call('session', opts);
      const url = `${trimSlash(accountsUrl)}/user/session?slim=true`;
      const raw = await request<SessionEnvelope>(c.step, 'GET', url, { signal: c.signal });
      return normalizeSession(raw, c.step, url);
    },
  };
}

// --- helpers --------------------------------------------------------------------------------------------------------

type SessionEnvelope = { success?: boolean; error?: unknown; results?: SessionResults } & Partial<SessionResults>;
interface SessionResults {
  user?: { unique?: string; name?: string; email?: string };
  token?: { value?: string; expired_time?: string };
  organization?: { id: number; name: string; accepted?: boolean } | null;
  license?: { type?: string; expired_time?: string | null } | null;
}

function normalizeSession(raw: SessionEnvelope, step: string, url: string): AccountSession {
  const r: SessionResults = raw?.results ?? raw ?? {};
  if (raw && raw.success === false) {
    throw new ApiError(step, `the accounts API rejected the session: ${JSON.stringify(raw.error ?? '')}`, { status: 200, body: JSON.stringify(raw), url, method: 'GET' });
  }
  const user = r.user ?? {};
  if (!user.unique && !user.email) {
    throw new ApiError(step, 'the accounts API returned no user for this token', { status: 200, body: JSON.stringify(raw), url, method: 'GET' });
  }
  const session: AccountSession = {
    user: { id: user.unique ?? '', name: user.name ?? '', email: user.email ?? '' },
    licenseType: r.license?.type ? r.license.type.toLowerCase() : null,
    raw,
  };
  if (r.token) {
    const t: { value?: string; expiredAt?: string } = {};
    if (r.token.value !== undefined) t.value = r.token.value;
    if (r.token.expired_time !== undefined) t.expiredAt = r.token.expired_time;
    session.token = t;
  }
  if (r.organization !== undefined) session.organization = r.organization;
  if (r.license?.expired_time !== undefined) session.licenseExpiresAt = r.license.expired_time;
  return session;
}

function parseErrorBody(text: string): { message?: string; code?: string } {
  try {
    const j = JSON.parse(text);
    if (j && typeof j === 'object') {
      return { message: typeof j.message === 'string' ? j.message : undefined, code: typeof j.code === 'string' ? j.code : undefined };
    }
  } catch { /* not JSON */ }
  return {};
}

/** One signal that aborts when any input aborts (AbortSignal.any is not in every runtime yet). */
export function combineSignals(signals: (AbortSignal | undefined | null)[]): AbortSignal | undefined {
  const live = signals.filter((s): s is AbortSignal => !!s);
  if (live.length === 0) return undefined;
  if (live.length === 1) return live[0];
  const anyFn = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyFn === 'function') return anyFn.call(AbortSignal, live);
  const c = new AbortController();
  for (const s of live) {
    if (s.aborted) { c.abort(s.reason); break; }
    s.addEventListener('abort', () => c.abort(s.reason), { once: true });
  }
  return c.signal;
}
